import { useCallback, useEffect, useMemo, useState } from "react";
import { sortObjectsByProperty } from "utils/sortUtils";
import { userFullName } from "utils/namingUtils";
import { useFieldsContext } from "context/FieldsProvider";
import { useCommonFieldsWith } from "./useCommonFieldsWith";

export const useAssociateFarmers = (farmerId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [associateFarmers, setAssociateFarmers] = useState();
  const { fields, fetchState } = useFieldsContext();

  useEffect(() => {
    setIsLoading(true);
    if (fetchState.isLoading) {
      return;
    }
    const associatedFarmers = {};
    fields.forEach((field) => {
      field.farmerDetails.forEach((farmer) => {
        const associatedFarmerRegistered = farmer.email in associatedFarmers;
        if (associatedFarmerRegistered) {
          associatedFarmers[farmer.email].fields = {
            ...associatedFarmers[farmer.email].fields,
            [field.id]: field,
          };
        } else {
          associatedFarmers[farmer.email] = {
            ...farmer,
            name: userFullName({ user: farmer }),
            fields: { [field.id]: field },
          };
        }
      });
      return;
    });

    const newFarmers = Object.values(associatedFarmers)
      .sort((a, b) => sortObjectsByProperty(a, b, "name"))
      .map((farmer) => ({
        ...farmer,
        fields: Object.values(farmer.fields),
      }));

    setIsLoading(false);

    setAssociateFarmers(newFarmers);
  }, [fields, fetchState]);

  const selectedAssociate = useMemo(() => {
    if (!farmerId) return;
    return associateFarmers?.find((farmer) => farmer.farmerId === farmerId);
  }, [farmerId, associateFarmers]);

  const removeAssociate = useCallback((farmerId) => {
    if (!farmerId) return;
    setAssociateFarmers((prev) =>
      prev.filter((farmer) => farmer.farmerId !== farmerId)
    );
  }, []);

  const { getCommonFieldsWithFarmer } = useCommonFieldsWith();
  const getCommonFieldsWithAssociate = useCallback(() => {
    if (!selectedAssociate) return;

    return getCommonFieldsWithFarmer(selectedAssociate?.email);
  }, [getCommonFieldsWithFarmer, selectedAssociate]);

  return {
    isLoading,
    associateFarmers,
    selectedAssociate,
    removeAssociate,
    getCommonFieldsWithAssociate,
  };
};

export const useAssociateAgronomists = (agronomistId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [associateAgronomists, setAssociateAgronomists] = useState();
  const { fields, fetchState } = useFieldsContext();

  useEffect(() => {
    setIsLoading(true);
    if (fetchState.isLoading) {
      return;
    }
    const associatedAgronomists = {};
    fields.forEach((field) => {
      field.agronomistDetails.forEach((agronomist) => {
        const associatedAgronomistRegistered =
          agronomist.email in associatedAgronomists;
        if (associatedAgronomistRegistered) {
          associatedAgronomists[agronomist.email].fields = {
            ...associatedAgronomists[agronomist.email].fields,
            [field.id]: field,
          };
        } else {
          associatedAgronomists[agronomist.email] = {
            ...agronomist,
            name: userFullName({ user: agronomist }),
            fields: { [field.id]: field },
          };
        }
      });
      return;
    });

    const newAgronomists = Object.values(associatedAgronomists)
      .sort((a, b) => sortObjectsByProperty(a, b, "name"))
      .map((agronomist) => ({
        ...agronomist,
        fields: Object.values(agronomist.fields),
      }));

    setIsLoading(false);

    setAssociateAgronomists(newAgronomists);
  }, [fields, fetchState]);

  const selectedAssociate = useMemo(() => {
    if (!agronomistId) return;
    return associateAgronomists?.find(
      (agronomist) => agronomist.agronomistId === agronomistId
    );
  }, [agronomistId, associateAgronomists]);

  const removeAssociate = useCallback((agronomistId) => {
    if (!agronomistId) return;
    setAssociateAgronomists((prev) =>
      prev.filter((agronomist) => agronomist.agronomistId !== agronomistId)
    );
  }, []);

  const { getCommonFieldsWithAgronomist } = useCommonFieldsWith();
  const getCommonFieldsWithAssociate = useCallback(() => {
    if (!selectedAssociate) return;

    return getCommonFieldsWithAgronomist(selectedAssociate?.email);
  }, [getCommonFieldsWithAgronomist, selectedAssociate]);

  return {
    isLoading,
    associateAgronomists,
    selectedAssociate,
    removeAssociate,
    getCommonFieldsWithAssociate,
  };
};
